import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast, StandardDataGrid, gridActionButton } from './listPageStyles';

const API_BASE = '/api/vehicles/delivery-notes';

const STATUS_TABS = [
  { key: '', label: 'Tümü' },
  { key: 'DRAFT', label: 'Taslak' },
  { key: 'PRINTED', label: 'Basıldı' },
  { key: 'IN_TRANSIT', label: 'Yolda' },
  { key: 'COMPLETED', label: 'Tamamlandı' },
];

const STATUS_META = {
  DRAFT: { label: 'Taslak', tone: 'neutral' },
  PRINTED: { label: 'Basıldı', tone: 'warn' },
  IN_TRANSIT: { label: 'Yolda', tone: 'warn' },
  COMPLETED: { label: 'Tamamlandı', tone: 'ok' },
};

const PAGE_SIZE_DEFAULT = 20;

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    draftCount: pick(data, 'draftCount', 'DraftCount') ?? 0,
    printedCount: pick(data, 'printedCount', 'PrintedCount') ?? 0,
    inTransitCount: pick(data, 'inTransitCount', 'InTransitCount') ?? 0,
    completedCount: pick(data, 'completedCount', 'CompletedCount') ?? 0,
  };
}

function statusPillFor(status) {
  const meta = STATUS_META[status] || { label: status || '-', tone: 'neutral' };
  const tones = {
    ok: { color: '#0f5132', background: 'rgba(0, 200, 117, 0.12)', borderColor: 'rgba(0, 200, 117, 0.30)' },
    warn: { color: '#92400e', background: 'rgba(253, 171, 61, 0.14)', borderColor: 'rgba(253, 171, 61, 0.32)' },
    bad: { color: '#7f1d1d', background: 'rgba(226, 68, 92, 0.12)', borderColor: 'rgba(226, 68, 92, 0.30)' },
    neutral: { color: '#64748b', background: 'rgba(100, 116, 139, 0.14)', borderColor: 'rgba(100, 116, 139, 0.28)' },
  };
  const t = tones[meta.tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999, fontWeight: 800, fontSize: 12,
      border: `1px solid ${t.borderColor}`, background: t.background, color: t.color,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: 'currentColor', opacity: 0.85 }} />
      {meta.label}
    </span>
  );
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

function toIsoStart(d) { if (!d) return ''; try { return new Date(`${d}T00:00:00`).toISOString(); } catch { return ''; } }
function toIsoEnd(d) {
  if (!d) return '';
  try { const x = new Date(`${d}T00:00:00`); x.setDate(x.getDate() + 1); return x.toISOString(); } catch { return ''; }
}

function badge(label, value, tone) {
  const tones = {
    blue: { bg: 'rgba(0, 115, 234, 0.10)', border: 'rgba(0, 115, 234, 0.32)', color: '#1d4ed8', dot: 'var(--accent)' },
    green: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.32)', color: '#0f5132', dot: '#16a34a' },
    orange: { bg: 'rgba(253, 171, 61, 0.14)', border: 'rgba(253, 171, 61, 0.32)', color: '#92400e', dot: 'var(--warn)' },
    red: { bg: 'rgba(226, 68, 92, 0.12)', border: 'rgba(226, 68, 92, 0.30)', color: '#7f1d1d', dot: '#dc2626' },
    gray: { bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.28)', color: '#475569', dot: '#64748b' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '7px 12px', borderRadius: 999,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
      fontWeight: 800, fontSize: 12,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
      {label}: {value}
    </span>
  );
}

export function DeliveryNotesPageLegacy() {
  const [statusFilter, setStatusFilter] = useState('');
  const [plate, setPlate] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchText, setSearchText] = useState('');

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ draftCount: 0, printedCount: 0, inTransitCount: 0, completedCount: 0 });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState('CreatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedId, setSelectedId] = useState(null);

  const [detailRow, setDetailRow] = useState(null);
  const [detailLines, setDetailLines] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (plate.trim()) params.set('plate', plate.trim());
      const isoFrom = toIsoStart(dateFrom);
      const isoTo = toIsoEnd(dateTo);
      if (isoFrom) params.set('from', isoFrom);
      if (isoTo) params.set('to', isoTo);
      params.set('take', '500');

      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({
        draftCount: data.draftCount,
        printedCount: data.printedCount,
        inTransitCount: data.inTransitCount,
        completedCount: data.completedCount,
      });
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, plate, dateFrom, dateTo, showToast]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (row) => {
    setDetailRow(row);
    setDetailLines(null);
    setDetailLoading(true);
    try {
      const id = pick(row, 'id', 'Id');
      const res = await fetch(`${API_BASE}/${id}/details`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Satırlar alınamadı (${res.status})`);
      const payload = await res.json();
      const data = pick(payload, 'Data', 'data') ?? payload;
      setDetailLines(pick(data, 'items', 'Items') ?? []);
    } catch (err) {
      showToast('error', err.message || 'Satırlar alınamadı');
      setDetailLines([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const filteredSorted = useMemo(() => {
    const needle = searchText.trim().toLocaleLowerCase('tr-TR');
    const match = (row) => {
      if (!needle) return true;
      const fields = [
        pick(row, 'sdmName', 'SdmName'),
        pick(row, 'plate', 'Plate'),
        pick(row, 'noteNo', 'NoteNo'),
        pick(row, 'deliveryDate', 'DeliveryDate'),
      ];
      return fields.some(v => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const filtered = rows.filter(match);
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'SdmName': return pick(row, 'sdmName', 'SdmName') || '';
        case 'Plate': return pick(row, 'plate', 'Plate') || '';
        case 'NoteNo': return pick(row, 'noteNo', 'NoteNo') || '';
        case 'Status': return pick(row, 'status', 'Status') || '';
        case 'TotalCases': return Number(pick(row, 'totalCases', 'TotalCases')) || 0;
        case 'CreatedDate':
        default: {
          const v = pick(row, 'createdDate', 'CreatedDate');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, searchText, sortField, sortDir]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const columns = useMemo(() => [
    {
      field: 'createdDate',
      headerName: 'Oluşturma',
      flex: 0.9,
      minWidth: 150,
      valueGetter: ({ row }) => formatDate(pick(row, 'createdDate', 'CreatedDate')),
    },
    {
      field: 'noteNo',
      headerName: 'İrsaliye No',
      flex: 1,
      minWidth: 160,
      valueGetter: ({ row }) => pick(row, 'noteNo', 'NoteNo') || '(Henüz basılmadı)',
    },
    {
      field: 'sdmName',
      headerName: 'SDM',
      flex: 1.2,
      minWidth: 180,
      valueGetter: ({ row }) => pick(row, 'sdmName', 'SdmName') || '-',
    },
    {
      field: 'plate',
      headerName: 'Plaka',
      flex: 0.7,
      minWidth: 110,
      valueGetter: ({ row }) => pick(row, 'plate', 'Plate') || '-',
    },
    {
      field: 'status',
      headerName: 'Durum',
      flex: 0.8,
      minWidth: 140,
      renderCell: ({ row }) => statusPillFor(pick(row, 'status', 'Status')),
    },
    {
      field: 'totalCases',
      headerName: 'Koli',
      flex: 0.5,
      minWidth: 80,
      valueGetter: ({ row }) => pick(row, 'totalCases', 'TotalCases') ?? '-',
    },
    {
      field: 'totalUnits',
      headerName: 'Adet',
      flex: 0.5,
      minWidth: 80,
      valueGetter: ({ row }) => pick(row, 'totalUnits', 'TotalUnits') ?? '-',
    },
    {
      field: 'detailCount',
      headerName: 'Satır',
      flex: 0.5,
      minWidth: 80,
      valueGetter: ({ row }) => pick(row, 'detailCount', 'DetailCount') ?? 0,
    },
    {
      field: 'actions',
      headerName: 'Aksiyon',
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            style={{ ...gridActionButton, background: 'var(--accent)' }}
            onClick={() => openDetail(row)}
          >
            Detay
          </button>
        </div>
      ),
    },
  ], []);

  const totalCount = filteredSorted.length;

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h1 style={s.pageTitle}>Sevk İrsaliyeleri</h1>
          <p style={s.pageSubtitle}>
            Araç yüklemesi sonrası bastırılan irsaliyeler. Saha temsilcisi seri no'ları
            manuel girer; basım, sevkiyat ve teslimat aşamaları takip edilir.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {badge('Taslak', counts.draftCount, 'gray')}
          {badge('Basıldı', counts.printedCount, 'orange')}
          {badge('Yolda', counts.inTransitCount, 'blue')}
          {badge('Tamamlanan', counts.completedCount, 'green')}
          <button type="button" style={s.btnSecondary} onClick={load} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Yenile'}
          </button>
        </div>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          {STATUS_TABS.map(tab => (
            <button
              key={tab.key || 'all'}
              type="button"
              onClick={() => setStatusFilter(tab.key)}
              style={{
                ...(statusFilter === tab.key ? s.btnPrimary : s.btnSecondary),
                padding: '6px 14px', fontSize: 13,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div style={s.toolbarRight}>
          <input
            type="text"
            value={plate}
            onChange={(e) => setPlate(e.target.value)}
            placeholder="Plaka"
            style={{ ...s.filterInput, width: 110 }}
          />
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{ ...s.filterInput, width: 140 }}
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{ ...s.filterInput, width: 140 }}
          />
          <input
            type="search"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            placeholder="İrsaliye no / SDM..."
            style={s.filterInput}
          />
        </div>
      </div>

      <StandardDataGrid
        rows={pagedRows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={page}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
        selectedId={selectedId}
        onRowClick={(row) => setSelectedId(pick(row, 'id', 'Id'))}
        onRowDoubleClick={openDetail}
        exportFileName="irsaliyeler"
      />

      {detailRow && (
        <div style={s.overlay} onClick={() => { setDetailRow(null); setDetailLines(null); }}>
          <div style={{ ...s.modal, maxWidth: 780 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>
                {pick(detailRow, 'noteNo', 'NoteNo') || '(Henüz basılmadı)'} • {pick(detailRow, 'plate', 'Plate')}
              </h2>
              <button
                type="button"
                style={s.closeBtn}
                onClick={() => { setDetailRow(null); setDetailLines(null); }}
              >×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  <div>{statusPillFor(pick(detailRow, 'status', 'Status'))}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Sevkiyat Tarihi</label>
                  <div style={s.inputView}>{pick(detailRow, 'deliveryDate', 'DeliveryDate') || '-'}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>SDM</label>
                  <div style={s.inputView}>{pick(detailRow, 'sdmName', 'SdmName') || '-'}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Basım Zamanı</label>
                  <div style={s.inputView}>{formatDate(pick(detailRow, 'printedAt', 'PrintedAt'))}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Toplam Koli</label>
                  <div style={s.inputView}>{pick(detailRow, 'totalCases', 'TotalCases') ?? '-'}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Toplam Adet</label>
                  <div style={s.inputView}>{pick(detailRow, 'totalUnits', 'TotalUnits') ?? '-'}</div>
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Satırlar ({detailLines?.length ?? '...'})</label>
                {detailLoading && <div style={{ color: '#6b7280', fontSize: 13 }}>Yükleniyor...</div>}
                {!detailLoading && detailLines && (
                  <div style={{
                    border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden',
                    maxHeight: 320, overflowY: 'auto',
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead style={{ background: '#f9fafb' }}>
                        <tr>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Ürün</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Koli</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Adet</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Seri No</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailLines.length === 0 && (
                          <tr><td colSpan="4" style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Satır yok</td></tr>
                        )}
                        {detailLines.map((line, idx) => (
                          <tr key={pick(line, 'id', 'Id') || idx} style={{ borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none' }}>
                            <td style={{ padding: '8px 12px' }}>
                              <div style={{ fontWeight: 600 }}>{pick(line, 'productName', 'ProductName') || '-'}</div>
                              {pick(line, 'productCode', 'ProductCode') && (
                                <div style={{ fontSize: 11, color: '#6b7280' }}>{pick(line, 'productCode', 'ProductCode')}</div>
                              )}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>
                              {pick(line, 'cases', 'Cases') ?? '-'}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                              {pick(line, 'units', 'Units') ?? '-'}
                            </td>
                            <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                              {pick(line, 'serialNo', 'SerialNo') || <span style={{ color: '#cbd5e1' }}>-</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <div style={s.modalFooter}>
              <button
                type="button"
                style={s.btnSecondary}
                onClick={() => { setDetailRow(null); setDetailLines(null); }}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default DeliveryNotesPageLegacy;
